import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  applyDemoMatrixProfile,
  listDemoMatrixProfiles,
  previewDemoMatrixProfile,
  type DemoMatrixApplyResult,
  type DemoMatrixProfile,
} from '../services/apiService.ts';
import { isCanonicalPositiveTenantId } from '../services/demoMatrixRoute.ts';
import { demoUrl } from '../utils/demoUrl.ts';
import { toastError } from '../shared/ui/toast.ts';

const formatCodeLabel = (value?: string | null, fallback = '-') => {
  if (!value) {
    return fallback;
  }
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const renderChipRow = (items: string[] | undefined, emptyLabel: string) => {
  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {items.map((item) => (
        <Chip key={item} label={item} size="small" variant="outlined" />
      ))}
    </Box>
  );
};

// SR3-REG-3 (MA-28c): render each vertical as a clickable link to that vertical's live public demo
// (demoUrl builds demo.ospelio.ro/demo/start?vertical=<code>). This is the real consumer of the
// demoUrl helper that now lives in superadmin-frontend/src/utils.
const renderVerticalDemoLinks = (items: string[] | undefined, emptyLabel: string) => {
  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {items.map((item) => (
        <Link
          key={item}
          href={demoUrl(item)}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          title={`Open the live ${item} demo`}
        >
          <Chip
            label={item}
            size="small"
            variant="outlined"
            clickable
            icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          />
        </Link>
      ))}
    </Box>
  );
};

const DemoMatrixPage: React.FC = () => {
  const [profiles, setProfiles] = useState<DemoMatrixProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tenantId, setTenantId] = useState('');
  const [profileCode, setProfileCode] = useState('');
  const [applyFitnessFullData, setApplyFitnessFullData] = useState(false);
  const [applyVerticalBusinessData, setApplyVerticalBusinessData] = useState(false);

  const [previewCode, setPreviewCode] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<DemoMatrixProfile | null>(null);
  const [applyResult, setApplyResult] = useState<DemoMatrixApplyResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listDemoMatrixProfiles();
      setProfiles(rows);
      setProfileCode((current) => current || rows[0]?.code || '');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Demo matrix profiles could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedProfile = useMemo(
    () => profiles.find((entry) => entry.code === profileCode) || null,
    [profiles, profileCode]
  );
  const normalizedTenantId = tenantId.trim();
  const tenantIdIsValid = isCanonicalPositiveTenantId(normalizedTenantId);

  const runAction = async (action: () => Promise<unknown>) => {
    setActionLoading(true);
    setError(null);
    try {
      await action();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Demo matrix action failed.';
      setError(message);
      toastError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePreview = (code: string) =>
    runAction(async () => {
      const result = await previewDemoMatrixProfile(code);
      setPreviewCode(code);
      setPreviewResult(result);
    });

  const handleUseProfile = (code: string) => {
    setProfileCode(code);
    setApplyResult(null);
  };

  const handleDryRun = () => {
    if (!selectedProfile || !tenantIdIsValid) {
      return undefined;
    }
    return runAction(async () => {
      const result = await applyDemoMatrixProfile(normalizedTenantId, selectedProfile.code, {
        dryRun: true,
        applyFitnessFullData,
        applyVerticalBusinessData,
      });
      setApplyResult(result);
    });
  };

  const canSubmit = selectedProfile !== null && tenantIdIsValid && !actionLoading;

  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Superadmin / Demo Seeding
        </Typography>
        <Typography variant="h4">Demo Tenant Matrix</Typography>
        <Typography color="text.secondary">
          Preset combinations of verticals, packages, and expected routes for demo tenants. This console is
          read-only: it supports profile previews and non-mutating dry-runs.
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Preview profile for tenant
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Real apply is local-script-only and requires an explicit backend switch on an exact loopback origin.
          It is not available from this console.
        </Alert>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="Tenant ID"
            size="small"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            error={tenantId.length > 0 && !tenantIdIsValid}
            helperText={tenantId.length > 0 && !tenantIdIsValid ? 'Enter a positive tenant ID without spaces.' : ' '}
            inputProps={{ inputMode: 'numeric', pattern: '[1-9][0-9]*' }}
            sx={{ width: 160 }}
          />
          <TextField
            select
            SelectProps={{ native: true }}
            label="Demo profile"
            size="small"
            value={profileCode}
            onChange={(event) => setProfileCode(event.target.value)}
            sx={{ minWidth: 280 }}
          >
            {profiles.map((entry) => (
              <option key={entry.code} value={entry.code}>
                {entry.label || entry.code}
              </option>
            ))}
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={0}>
          <FormControlLabel
            control={
              <Checkbox
                checked={applyFitnessFullData}
                onChange={(event) => setApplyFitnessFullData(event.target.checked)}
              />
            }
            label="Apply Fitness full demo data (Fitness profiles only)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={applyVerticalBusinessData}
                onChange={(event) => setApplyVerticalBusinessData(event.target.checked)}
              />
            }
            label="Apply vertical business data (where implemented)"
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
          <Button variant="text" disabled={!profileCode || actionLoading} onClick={() => handlePreview(profileCode)}>
            Preview selected profile
          </Button>
          <Button variant="outlined" disabled={!canSubmit} onClick={handleDryRun}>
            Dry-run apply
          </Button>
        </Stack>
        {selectedProfile ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {selectedProfile.description || 'No description provided.'}
          </Typography>
        ) : null}
      </Paper>

      {previewResult ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Profile preview: {previewResult.label || previewCode}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {previewResult.description || 'No description provided.'}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Verticals
              </Typography>
              {renderChipRow(previewResult.verticals, 'No verticals configured.')}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Packages
              </Typography>
              {renderChipRow(previewResult.packages, 'No packages configured.')}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Expected scopes
              </Typography>
              {renderChipRow(previewResult.expectedScopes, 'No scopes configured.')}
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Expected routes
              </Typography>
              {renderChipRow(previewResult.expectedRoutes, 'No expected routes.')}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Forbidden routes
              </Typography>
              {renderChipRow(previewResult.forbiddenRoutes, 'No forbidden routes.')}
            </Box>
          </Stack>
          <Chip
            sx={{ mt: 2 }}
            size="small"
            label={`Data depth: ${formatCodeLabel(previewResult.dataDepth)}`}
            color={previewResult.dataDepth === 'FULL_DEMO' ? 'success' : 'default'}
          />
        </Paper>
      ) : null}

      {applyResult ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {applyResult.dryRun ? 'Planned changes' : 'Applied changes'} for {applyResult.profileCode}
          </Typography>
          <Stack spacing={0.75} sx={{ mb: 2 }}>
            {(applyResult.dryRun ? applyResult.plannedChanges : applyResult.appliedChanges).map((entry, index) => (
              <Typography key={`${entry}-${index}`} variant="body2">
                {entry}
              </Typography>
            ))}
            {(applyResult.dryRun ? applyResult.plannedChanges : applyResult.appliedChanges).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No changes reported.
              </Typography>
            ) : null}
          </Stack>

          {applyResult.verticalSeedSummaries.length > 0 ? (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Vertical seed summaries
              </Typography>
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {applyResult.verticalSeedSummaries.map((summary) => (
                  <Box key={summary.code}>
                    <Typography variant="body2" fontWeight={600}>
                      {summary.label} ({summary.verticalCode}) &mdash; {formatCodeLabel(summary.dataDepth)}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {(summary.dryRun ? summary.plannedChanges : summary.appliedChanges).map((entry, index) => (
                        <Typography key={`${summary.code}-${entry}-${index}`} variant="body2" color="text.secondary">
                          {entry}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </>
          ) : null}

          {applyResult.warnings.length > 0 ? (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Warnings
              </Typography>
              <Stack spacing={0.75}>
                {applyResult.warnings.map((warning, index) => (
                  <Alert key={`${warning.code}-${index}`} severity="warning" variant="outlined">
                    {warning.code}: {warning.message}
                  </Alert>
                ))}
              </Stack>
            </>
          ) : null}
        </Paper>
      ) : null}

      <Paper sx={{ overflowX: 'auto' }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <Typography variant="h6">Demo profiles catalog</Typography>
          <Typography variant="caption" color="text.secondary">
            Tip: click a vertical chip to open its live public demo in a new tab.
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Profile</TableCell>
              <TableCell>Verticals</TableCell>
              <TableCell>Packages</TableCell>
              <TableCell>Data depth</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No demo profiles configured.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              profiles.map((entry) => (
                <TableRow key={entry.code} selected={entry.code === profileCode}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {entry.label || entry.code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.code}
                    </Typography>
                    {entry.description ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {entry.description}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{renderVerticalDemoLinks(entry.verticals, 'Shared')}</TableCell>
                  <TableCell>{renderChipRow(entry.packages, 'None')}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={formatCodeLabel(entry.dataDepth)}
                      color={entry.dataDepth === 'FULL_DEMO' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="text" disabled={actionLoading} onClick={() => handlePreview(entry.code)}>
                        Preview
                      </Button>
                      <Button
                        size="small"
                        variant={entry.code === profileCode ? 'contained' : 'outlined'}
                        onClick={() => handleUseProfile(entry.code)}
                      >
                        Use profile
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};

export default DemoMatrixPage;
